import React, { useCallback, useEffect, useState } from 'react';
import AttachmentList from './AttachmentList';
import { API_URL } from './uploads';
import './Submissions.css';

export const STATUS_LABELS = {
  submitted: 'Awaiting review',
  approved: 'Approved',
  changes_requested: 'Changes requested',
};

async function api(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Request failed (${res.status})`);
  return data;
}

const formatDate = (iso) => new Date(iso).toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
});

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      // Older browsers / insecure origins: fall back to a hidden textarea
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button type="button" className="sub-copy-btn" onClick={copy}>
      {copied ? 'Copied ✓' : 'Copy'}
    </button>
  );
}

// One submission card. Brands review `submitted` items; creators can withdraw
// anything not yet approved and resubmit after changes are requested; agents
// only read.
function SubmissionCard({ submission: s, role, showCampaign, showCreator, onChanged, onResubmit }) {
  const [mode, setMode] = useState(null); // null | 'changes'
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const review = async (status) => {
    if (status === 'changes_requested' && !feedback.trim()) {
      setError('Tell the creator what to change.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api(`/api/submissions/${s.id}/review`, {
        method: 'POST',
        body: { status, feedback: feedback.trim() || undefined },
      });
      setMode(null);
      setFeedback('');
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (!window.confirm(`Withdraw "${s.title || 'this submission'}"? The brand will no longer see it.`)) return;
    setBusy(true);
    setError('');
    try {
      await api(`/api/submissions/${s.id}`, { method: 'DELETE' });
      onChanged();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className={`sub-card status-${s.status}`}>
      <div className="sub-card-header">
        <div className="sub-card-who">
          {showCreator && <strong>{s.influencerName || 'Creator'}</strong>}
          {showCampaign && <span className="sub-card-campaign">{s.jobTitle}</span>}
          <span className="sub-card-title">{s.title || 'Untitled submission'}</span>
        </div>
        <div className="sub-card-side">
          <span className={`sub-status ${s.status}`}>{STATUS_LABELS[s.status] || s.status}</span>
          <span className="sub-date">{formatDate(s.createdAt)}</span>
        </div>
      </div>

      {s.caption && (
        <div className="sub-section">
          <div className="sub-section-head">
            <span className="sub-label">Post caption</span>
            <CopyButton text={s.caption} />
          </div>
          <p className="sub-caption">{s.caption}</p>
        </div>
      )}

      {s.files?.length > 0 && (
        <div className="sub-section">
          <span className="sub-label">Files ({s.files.length})</span>
          <AttachmentList attachments={s.files} />
        </div>
      )}

      {s.links?.length > 0 && (
        <div className="sub-section">
          <span className="sub-label">Links</span>
          <ul className="sub-links">
            {s.links.map((link) => (
              <li key={link}>
                <a href={link} target="_blank" rel="noopener noreferrer">{link}</a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {s.notes && (
        <div className="sub-section">
          <span className="sub-label">Notes</span>
          <p className="sub-notes">{s.notes}</p>
        </div>
      )}

      {s.feedback && (
        <div className="sub-feedback">
          <span className="sub-label">
            Brand feedback{s.reviewedAt ? ` · ${formatDate(s.reviewedAt)}` : ''}
          </span>
          <p>{s.feedback}</p>
        </div>
      )}

      {error && <div className="sub-error">{error}</div>}

      {role === 'brand' && s.status === 'submitted' && (
        mode === 'changes' ? (
          <div className="sub-review-form">
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              placeholder="What should the creator change?"
              autoFocus
            />
            <div className="sub-card-actions">
              <button type="button" className="sub-btn secondary" onClick={() => { setMode(null); setError(''); }} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="sub-btn warn" onClick={() => review('changes_requested')} disabled={busy}>
                {busy ? 'Sending...' : 'Send feedback'}
              </button>
            </div>
          </div>
        ) : (
          <div className="sub-card-actions">
            <button type="button" className="sub-btn secondary" onClick={() => setMode('changes')} disabled={busy}>
              Request changes
            </button>
            <button type="button" className="sub-btn primary" onClick={() => review('approved')} disabled={busy}>
              {busy ? 'Approving...' : 'Approve'}
            </button>
          </div>
        )
      )}

      {role === 'influencer' && s.status !== 'approved' && (
        <div className="sub-card-actions">
          <button type="button" className="sub-btn link danger" onClick={withdraw} disabled={busy}>
            Withdraw
          </button>
          {s.status === 'changes_requested' && onResubmit && (
            <button type="button" className="sub-btn primary" onClick={() => onResubmit(s)} disabled={busy}>
              Submit a revised version
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Lists content submissions for a campaign / bid / status (the API scopes
// them by role). Re-fetches when `query` values or `reloadKey` change.
function SubmissionsPanel({
  query = {},
  role,
  showCampaign = false,
  showCreator = role !== 'influencer',
  reloadKey = 0,
  emptyText = 'No content submitted yet.',
  onChanged,
  onResubmit,
}) {
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { jobId, bidId, status } = query;

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (jobId) params.set('jobId', jobId);
    if (bidId) params.set('bidId', bidId);
    if (status) params.set('status', status);
    try {
      const data = await api(`/api/submissions?${params.toString()}`);
      setSubmissions(data.submissions || []);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [jobId, bidId, status]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const handleChanged = () => {
    load();
    if (onChanged) onChanged();
  };

  if (isLoading) return <p className="sub-empty">Loading submissions...</p>;
  if (error) return <div className="sub-error">{error}</div>;
  if (submissions.length === 0) return <p className="sub-empty">{emptyText}</p>;

  return (
    <div className="sub-list">
      {submissions.map((s) => (
        <SubmissionCard
          key={s.id}
          submission={s}
          role={role}
          showCampaign={showCampaign}
          showCreator={showCreator}
          onChanged={handleChanged}
          onResubmit={onResubmit}
        />
      ))}
    </div>
  );
}

export default SubmissionsPanel;
